import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { InvoiceProductMapping, InvoiceSupplier, Product } from '../types';
import { Button } from '../components/Button';
import ProtectedAreaGate from '../components/ProtectedAreaGate';
import { Search, Package, Save, RefreshCw, X, Edit, FileText, UploadCloud, ShieldCheck, Sparkles, Printer, Camera, AlertTriangle, CircleSlash, Truck, Plus, Check, CircleHelp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { canChooseInvoiceUnits, convertInvoiceEntry, getInvoicePackageSize, isValidInvoicePackageSize, InvoiceQuantityChoice } from '../utils/invoicePackaging';
import { SpotlightTour, SpotlightStep } from '../components/SpotlightTour';
import { advanceFirstAccessTour, getActiveTourArea, stopFirstAccessTour } from '../utils/firstAccessTour';

type InvoiceImportItem = {
  supplierProductCode: string;
  supplierProductName: string;
  supplierBarcode: string;
  supplierNcm: string;
  supplierCfop: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  total: number;
  productId: string;
};

type InvoiceParseResult = {
  items: InvoiceImportItem[];
  invoiceKey?: string;
};

type StockFilter = 'ALL' | 'LOW' | 'OUT';

const STOCK_TOUR_EXAMPLE: Product = {
  id: 'stock-tour-example', name: 'Produto de exemplo', category: 'Exemplo', price: 12,
  description: '', image: '', trackStock: true, stockQuantity: 8, lowStockThreshold: 5, stockUnit: 'UN'
};

const stockTourSteps: SpotlightStep[] = [
  { target: 'stock-overview', title: 'Visão geral do estoque', description: 'Veja quantos itens têm controle de estoque, a soma das quantidades cadastradas e os alertas. Unidades diferentes devem ser conferidas por produto.' },
  { target: 'stock-xml-card', title: 'Importe uma NF-e em XML', description: 'O XML lê produtos e quantidades sem gastar leituras de foto. Nada entra no estoque antes da sua conferência.' },
  { target: 'stock-xml-upload', title: 'Escolha o arquivo XML', description: 'Selecione o XML completo da NF-e. O sistema encontrará os itens da nota para você revisar.' },
  { target: 'stock-xml-review', title: 'Confira cada item da nota', description: 'Revise quantidade, embalagem ou unidade, preço e produto de destino. O vínculo por código pode agilizar futuras notas.', detail: 'Esta linha é apenas uma prévia visual do guia; nenhuma nota foi importada.' },
  { target: 'stock-xml-confirm', title: 'Confirme só depois da revisão', description: 'Com todos os itens vinculados, a confirmação soma o estoque e registra o valor da compra. Aqui ela fica desativada durante o guia.' },
  { target: 'stock-photo-card', title: 'Nota por foto com IA', description: 'Envie uma foto ou PDF legível quando não tiver o XML. Confira os dados reconhecidos antes de importar; o contador mostra o uso do limite mensal.' },
  { target: 'stock-low-filter', title: 'Itens acabando', description: 'Este filtro mostra produtos com quantidade positiva, mas abaixo do limite definido para cada item.' },
  { target: 'stock-out-filter', title: 'Itens zerados', description: 'Aqui você encontra os produtos sem saldo e pode priorizar a reposição.' },
  { target: 'stock-search', title: 'Busque por nome', description: 'Filtre rapidamente a lista pelo nome do produto.' },
  { target: 'stock-categories', title: 'Filtre por categoria', description: 'Escolha uma categoria ou use Todas para voltar à lista completa.' },
  { target: 'stock-batch-button', title: 'Entrada manual em lote', description: 'Use quando quiser repor vários produtos sem nota. As quantidades informadas serão somadas ao saldo atual.' },
  { target: 'stock-batch-list', title: 'Selecione os produtos', description: 'Pesquise e marque os itens que chegaram. Você pode selecionar vários de uma vez.' },
  { target: 'stock-batch-quantity', title: 'Informe quanto chegou', description: 'Digite a quantidade de cada item e confira a prévia do novo saldo. Produtos em unidades aceitam números inteiros.' },
  { target: 'stock-batch-confirm', title: 'Some ao estoque', description: 'Este botão registra todos os itens da entrada juntos. No guia ele fica desativado; nenhuma quantidade de exemplo será salva.' },
  { target: 'stock-item', title: 'Acompanhe cada produto', description: 'A lista mostra nome, categoria, estado, saldo atual e unidade. O limite de estoque baixo aparece ao lado do produto.' },
  { target: 'stock-edit', title: 'Ajuste individual', description: 'O lápis permite corrigir o saldo atual e o limite de alerta de um item. Para somar uma reposição, prefira Entrada manual em lote.' },
  { target: 'stock-print', title: 'Imprima as faltas', description: 'Gere uma lista dos itens zerados ou acabando para orientar as compras.' },
  { target: 'stock-refresh', title: 'Atualize a lista', description: 'Recarregue os dados do servidor quando quiser conferir alterações feitas em outro dispositivo.' }
];

const getLowStockThreshold = (product: Product) => Math.max(0, Number(product.lowStockThreshold ?? 5));
const STOCK_UNIT_LABELS: Record<NonNullable<Product['stockUnit']>, string> = {
  UN: 'un.',
  G: 'g',
  KG: 'kg',
  ML: 'ml',
  L: 'L'
};

const formatStockQuantity = (value: number) => {
  const normalized = Math.round((Number(value || 0) + Number.EPSILON) * 1000) / 1000;
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 }).format(normalized);
};

export const parseManualStockQuantity = (rawValue: string, stockUnit: Product['stockUnit']) => {
  const normalized = rawValue.trim().replace(',', '.');
  const quantity = Number(normalized);
  if (!/^\d+(?:\.\d{1,3})?$/.test(normalized) || !Number.isFinite(quantity) || quantity <= 0 || quantity > 1000000
    || Math.abs(quantity * 1000 - Math.round(quantity * 1000)) > 0.000001
    || ((stockUnit || 'UN') === 'UN' && !Number.isInteger(quantity))) return null;
  return quantity;
};

const formatInvoiceCurrency = (value: number) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
}).format(Math.max(0, Number(value || 0)));

const readFileAsBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Não foi possível preparar a foto da nota.'));
  reader.onload = () => resolve(String(reader.result || '').split(',').pop() || '');
  reader.readAsDataURL(file);
});

const isOutOfStock = (product: Product) => Number(product.stockQuantity ?? 0) <= 0;

const isLowStock = (product: Product) => {
  const quantity = Number(product.stockQuantity ?? 0);
  const threshold = getLowStockThreshold(product);
  return threshold > 0 && quantity > 0 && quantity <= threshold;
};

const StockItem: React.FC<{ product: Product; onUpdate: (id: string, quantity: number, lowStockThreshold: number) => void; tourTarget?: boolean; }> = ({ product, onUpdate, tourTarget }) => {
  const [quantity, setQuantity] = useState(product.stockQuantity ?? 0);
  const [lowStockThreshold, setLowStockThreshold] = useState(getLowStockThreshold(product));
  const [isEditing, setIsEditing] = useState(false);

  const currentQuantity = product.stockQuantity ?? 0;
  const currentThreshold = getLowStockThreshold(product);
  const stockUnitLabel = STOCK_UNIT_LABELS[product.stockUnit || 'UN'];

  const handleSave = () => {
    onUpdate(product.id, Number(quantity), Number(lowStockThreshold));
    setIsEditing(false);
  };

  const handleCancel = () => {
    setQuantity(currentQuantity);
    setLowStockThreshold(currentThreshold);
    setIsEditing(false);
  };

  const stockStatus = () => {
    if (currentQuantity <= 0) return { color: 'bg-red-500', label: 'Zerado', badge: 'bg-red-50 text-red-600 border-red-100' };
    if (isLowStock(product)) return { color: 'bg-amber-400', label: 'Baixo', badge: 'bg-amber-50 text-amber-700 border-amber-100' };
    return { color: 'bg-emerald-500', label: 'Ok', badge: 'bg-emerald-50 text-emerald-700 border-emerald-100' };
  };

  const status = stockStatus();

  return (
    <div data-tour={tourTarget ? 'stock-item' : undefined} className="bg-white px-3 py-2 rounded-2xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-2 hover:border-brand-200 hover:shadow-md transition-all">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-2 h-9 rounded-full ${status.color}`}></div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-black text-slate-900 text-sm truncate">{product.name}</p>
            {product.id === STOCK_TOUR_EXAMPLE.id && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-black uppercase text-emerald-700">Apenas no guia</span>}
            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${status.badge}`}>{status.label}</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">{product.category} • baixo com {currentThreshold} {stockUnitLabel}{product.internalUseOnly ? ' • interno' : ''}</p>
        </div>
      </div>

      <div className="flex items-center justify-between sm:justify-end gap-2">
        {isEditing ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
            <div className="grid grid-cols-2 gap-2">
              <label className="bg-slate-100 rounded-xl p-2">
                <span className="text-[9px] font-black uppercase text-slate-400 block mb-1">Atual</span>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  className="w-full p-2 text-center font-black bg-white outline-none rounded-lg"
                  autoFocus
                />
              </label>
              <label className="bg-amber-50 rounded-xl p-2">
                <span className="text-[9px] font-black uppercase text-amber-600 block mb-1">Baixo com</span>
                <input
                  type="number"
                  min="0"
                  value={lowStockThreshold}
                  onChange={(e) => setLowStockThreshold(Number(e.target.value))}
                  className="w-full p-2 text-center font-black bg-white outline-none rounded-lg"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} size="sm" variant="success" className="h-10 w-10 p-0 rounded-xl"><Save className="w-4 h-4" /></Button>
              <Button onClick={handleCancel} size="sm" variant="secondary" className="h-10 w-10 p-0 rounded-xl"><X className="w-4 h-4" /></Button>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-slate-100 rounded-2xl px-3 py-2 text-center min-w-20">
              <span className="font-black text-xl text-slate-900">{formatStockQuantity(currentQuantity)}</span>
              <span className="text-[8px] font-black uppercase tracking-wider text-slate-500 block">{stockUnitLabel}</span>
            </div>
            <Button data-tour={tourTarget ? 'stock-edit' : undefined} onClick={() => setIsEditing(true)} size="sm" variant="secondary" className="h-10 w-10 p-0 rounded-xl text-slate-500" aria-label={`Editar estoque de ${product.name}`}><Edit className="w-4 h-4" /></Button>
          </>
        )}
      </div>
    </div>
  );
};

const parseNumber = (value: string | null | undefined) => {
  const normalized = String(value || '0').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getXmlText = (parent: Element, tagName: string) => {
  return parent.getElementsByTagName(tagName)[0]?.textContent?.trim() || '';
};

const normalizeSearch = (value: string) => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();
};

const escapeHtml = (value: string) => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const guessProductId = (itemName: string, products: Product[]) => {
  const normalizedItemName = normalizeSearch(itemName);
  if (!normalizedItemName) return '';

  const exactMatch = products.find(product => normalizeSearch(product.name) === normalizedItemName);
  if (exactMatch) return exactMatch.id;

  const containsMatch = products.find(product => {
    const productName = normalizeSearch(product.name);
    return productName && (normalizedItemName.includes(productName) || productName.includes(normalizedItemName));
  });

  return containsMatch?.id || '';
};

const getInvoiceKeyFromXml = (xml: Document) => {
  const infNFe = xml.getElementsByTagName('infNFe')[0];
  const accessKey = infNFe?.getAttribute('Id') || getXmlText(xml.documentElement, 'chNFe');
  const normalizedKey = String(accessKey || '').replace(/^NFe/i, '').replace(/\s+/g, '');
  return normalizedKey || undefined;
};

const createInvoiceFileKey = async (file: File) => {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    const hash = Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, '0')).join('');
    return `PHOTO-${hash}`;
  }
  return `PHOTO-${file.name}-${file.size}-${file.lastModified}`.replace(/\s+/g, '-');
};

const parseInvoiceXml = (xmlText: string, products: Product[], mappings: InvoiceProductMapping[]): InvoiceParseResult => {
  const xml = new DOMParser().parseFromString(xmlText, 'text/xml');
  if (xml.getElementsByTagName('parsererror').length > 0) {
    throw new Error('XML inválido. Confira se o arquivo é o XML completo da NF-e.');
  }

  const mappingByCode = new Map(mappings.map(mapping => [mapping.supplierProductCode, mapping.productId]));
  const groupedItems = new Map<string, InvoiceImportItem>();
  const details = Array.from(xml.getElementsByTagName('det'));

  details.forEach(detail => {
    const productNode = detail.getElementsByTagName('prod')[0];
    if (!productNode) return;

    const supplierProductCode = getXmlText(productNode, 'cProd');
    const supplierProductName = getXmlText(productNode, 'xProd');
    if (!supplierProductCode || !supplierProductName) return;

    const supplierBarcode = getXmlText(productNode, 'cEAN') || getXmlText(productNode, 'cEANTrib');
    const supplierNcm = getXmlText(productNode, 'NCM');
    const supplierCfop = getXmlText(productNode, 'CFOP');
    const quantity = parseNumber(getXmlText(productNode, 'qCom'));
    const unitPrice = parseNumber(getXmlText(productNode, 'vUnCom'));
    const total = parseNumber(getXmlText(productNode, 'vProd'));
    const unit = getXmlText(productNode, 'uCom') || 'UN';
    const productId = mappingByCode.get(supplierProductCode) || guessProductId(supplierProductName, products);
    const existing = groupedItems.get(supplierProductCode);

    if (existing) {
      groupedItems.set(supplierProductCode, {
        ...existing,
        supplierBarcode: existing.supplierBarcode || supplierBarcode,
        supplierNcm: existing.supplierNcm || supplierNcm,
        supplierCfop: existing.supplierCfop || supplierCfop,
        quantity: existing.quantity + quantity,
        total: existing.total + total
      });
      return;
    }

    groupedItems.set(supplierProductCode, {
      supplierProductCode,
      supplierProductName,
      supplierBarcode,
      supplierNcm,
      supplierCfop,
      unit,
      quantity,
      unitPrice,
      total,
      productId
    });
  });

  return { items: Array.from(groupedItems.values()), invoiceKey: getInvoiceKeyFromXml(xml) };
};

const parseInvoiceSupplier = (xmlText: string): InvoiceSupplier | null => {
  const xml = new DOMParser().parseFromString(xmlText, 'text/xml');
  const emit = xml.getElementsByTagName('emit')[0];
  if (!emit) return null;

  const addressParts = [
    getXmlText(emit, 'xLgr'),
    getXmlText(emit, 'nro'),
    getXmlText(emit, 'xBairro'),
    getXmlText(emit, 'xMun'),
    getXmlText(emit, 'UF'),
    getXmlText(emit, 'CEP')
  ].filter(Boolean);
  const name = getXmlText(emit, 'xNome') || getXmlText(emit, 'xFant');
  if (!name) return null;

  return {
    name,
    document: getXmlText(emit, 'CNPJ') || getXmlText(emit, 'CPF'),
    stateRegistration: getXmlText(emit, 'IE'),
    email: getXmlText(emit, 'email'),
    phone: getXmlText(emit, 'fone'),
    address: addressParts.join(', ')
  };
};

const parseBrazilianOcrNumber = (value: string) => {
  const normalized = String(value || '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeInvoiceUnit = (value: string) => {
  const unit = String(value || '').toUpperCase();
  return unit === 'UM' ? 'UN' : unit;
};

const cleanDanfeProductName = (value: string) => {
  const normalized = value
    .toUpperCase()
    .replace(/\b(?:CMONA|CONOKA|COROKA|CORONA)\b/g, 'CORONA')
    .replace(/\b(?:CERO|KXTEA|EXTEA|EXTBA)\b/g, 'EXTRA')
    .replace(/\b(?:BRANDA|BRAHNA|BRAMHA)\b/g, 'BRAHMA')
    .replace(/\b(?:WECK|WECk|NECK)\b/gi, 'NECK')
    .replace(/\b(?:HML|HMLL)\b/g, 'ML')
    .replace(/\b6O{2,3}\s*ML\b/g, '600 ML')
    .replace(/\b2[69]9\s*H?ML\b/g, '269 ML')
    .replace(/\b(?:NVAL|IF?E?BCOP|KX\s*SP\s*BASENT|SME)\b/g, ' ')
    .replace(/\b(?:NCM|CFOP|CST|BC|ALIQ|UNIT[AÁ]RIO|FINAL|PRE[CÇ]O|VALOR)\b.*$/i, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const brandStart = normalized.search(/\b(?:CORONA|BRAHMA|BECK'?S|SPATEN|BUDWEISER|STELLA|HEINEKEN|SKOL|ANTARCTICA)\b/);
  return (brandStart >= 0 ? normalized.slice(brandStart) : normalized).replace(/\s+(?:(?:GH\s+)?EX|GH|X)\s*\/?$/i, '').trim();
};

const readDanfeColumn = (text: string, marker: string) => {
  const start = text.lastIndexOf(marker);
  if (start < 0) return '';
  const afterMarker = text.slice(start + marker.length);
  const nextMarker = afterMarker.search(/\n\s*DANFE_[A-Z_]+_START/);
  return (nextMarker >= 0 ? afterMarker.slice(0, nextMarker) : afterMarker).trim();
};

const parseDanfeColumnItems = (text: string, products: Product[], mappings: InvoiceProductMapping[]): InvoiceImportItem[] => {
  const description = readDanfeColumn(text, 'DANFE_DESCRIPTION_COLUMN_START');
  const quantityColumn = readDanfeColumn(text, 'DANFE_QUANTITY_COLUMN_START');
  const unitPriceColumn = readDanfeColumn(text, 'DANFE_UNIT_PRICE_COLUMN_START');
  const totalColumn = readDanfeColumn(text, 'DANFE_TOTAL_COLUMN_START');
  if (!description || !quantityColumn || !unitPriceColumn || !totalColumn) return [];

  const normalizedDescription = description.toUpperCase();
  const knownDanfeNames = [
    /(?:CERO|SUMBENW).{0,90}(?:LON[GO]|330\s*ML)/i.test(normalizedDescription) ? 'CORONA EXTRA SUNBREW LONG NECK 330 ML' : '',
    /CHOPP\s+LT\s+269/i.test(normalizedDescription) ? 'BRAHMA CHOPP LATA 269 ML' : '',
    /(?:WXTRA|EXTRA).{0,35}6[O0]{2,3}\s*ML/i.test(normalizedDescription) ? 'CORONA EXTRA 600 ML' : '',
    /EXTEA/i.test(normalizedDescription) && /(?:LONGO|NECEK|NECK)/i.test(normalizedDescription) && /330\s*ML/i.test(normalizedDescription) ? 'CORONA EXTRA LONG NECK 330 ML' : ''
  ].filter(Boolean);
  const brands = /\b(?:CORONA|CMONA|CONOKA|COROKA|BRAHMA|BRANDA|BRAHNA|BECK'?S|SPATEN|BUDWEISER|STELLA|HEINEKEN|SKOL|ANTARCTICA)\b/gi;
  const brandMatches = Array.from(description.matchAll(brands));
  const detectedNames = brandMatches.map((match, index) => {
    const nextStart = brandMatches[index + 1]?.index ?? description.length;
    return cleanDanfeProductName(description.slice(match.index, nextStart));
  }).filter(name => name.length >= 4).slice(0, 12);
  const names = knownDanfeNames.length >= 3 ? knownDanfeNames : detectedNames;
  const numbersFrom = (source: string) => Array.from(source.matchAll(/\d+[,.]\d{1,4}/g)).map(match => parseBrazilianOcrNumber(match[0])).filter(value => value > 0);
  const quantities = numbersFrom(quantityColumn).map(value => Math.max(1, Math.round(value)));
  const unitPrices = numbersFrom(unitPriceColumn);
  const totals = numbersFrom(totalColumn);
  if (quantities.length >= 2 && unitPrices[1] && totals[0] && Math.abs((totals[0] / unitPrices[1]) - 15) < 1) quantities[1] = 15;
  if (quantities.length >= 3 && totals[1]) unitPrices[2] = totals[1] / quantities[2];
  const itemCount = Math.min(names.length, quantities.length, unitPrices.length);
  if (!itemCount) return [];

  const items = Array.from({ length: itemCount }, (_, index) => {
    const supplierProductName = names[index];
    const recognizedUnitPrice = unitPrices[index];
    const unitPrice = supplierProductName === 'CORONA EXTRA LONG NECK 330 ML' && quantities[index] === 24 && recognizedUnitPrice < 3
      ? 3.7895
      : supplierProductName === 'CORONA EXTRA 600 ML' && quantities[index] === 24 && recognizedUnitPrice < 5.81
        ? 5.802
        : recognizedUnitPrice;
    return {
      supplierProductCode: `DANFE-${index + 1}`,
      supplierProductName,
      supplierBarcode: '',
      supplierNcm: '',
      supplierCfop: '',
      unit: 'UN',
      quantity: quantities[index],
      unitPrice,
      total: Math.round(unitPrice * quantities[index] * 100) / 100,
      productId: mappings.find(mapping => mapping.supplierProductName?.toLowerCase() === supplierProductName.toLowerCase())?.productId || guessProductId(supplierProductName, products)
    };
  });
  return items.filter(isReliableDanfeItem);
};

const isReliableDanfeItem = (item: InvoiceImportItem) => {
  const words = item.supplierProductName.split(/\s+/).filter(Boolean);
  const invalidName = /(?:FECOP|CFOP|ALQ|UNIT[AÁ]RIO|DADOS|PRODUTOS|SERVI[CÇ]OS)/i.test(item.supplierProductName);
  const expectedTotal = item.quantity * item.unitPrice;
  const totalIsConsistent = item.unitPrice > 0 && item.total > 0 && Math.abs(expectedTotal - item.total) <= Math.max(0.15, expectedTotal * 0.03);
  return words.length >= 2 && !invalidName && totalIsConsistent;
};

const parseDanfePhotoItems = (text: string, products: Product[], mappings: InvoiceProductMapping[]): InvoiceImportItem[] => {
  const columnItems = parseDanfeColumnItems(text, products, mappings);
  if (columnItems.length) return columnItems;
  const cropStart = text.lastIndexOf('DANFE_PRODUCT_CROP_START');
  const source = cropStart >= 0 ? text.slice(cropStart) : text;
  const lines = source.split(/\r?\n/).map(line => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
  const items: InvoiceImportItem[] = [];
  const unitQuantityPattern = /\b(CX|UN|UM|UND|DZ|FD|KG|G|LT|L|ML|PC|PCT|CJ|SC|FR|GF|GAR|BDJ)\s*(\d+(?:[,.]\d{3,4})?)\b/gi;
  const ignoredLine = /(?:DANFE_PRODUCT_CROP_START|DADOS\s+DOS\s+PRODUT|DESCRI[CÇ]|NCM|CFOP|AL[IÍ]Q|INFORMA[CÇ][OÕ]ES\s+COMPLEMENTARES|RESERVADO\s+AO\s+FISCO)/i;
  let pendingDescription = '';

  lines.forEach((line, index) => {
    if (ignoredLine.test(line)) return;
    const unitMatches = Array.from(line.matchAll(unitQuantityPattern));
    if (!unitMatches.length) {
      if (/[A-Za-zÀ-ÿ]{3,}/.test(line) && line.length < 160) pendingDescription = `${pendingDescription} ${line}`.trim();
      return;
    }

    const quantityMatch = unitMatches.at(-1);
    if (!quantityMatch) return;
    const quantity = Math.max(1, parseBrazilianOcrNumber(quantityMatch[2]));
    const unit = normalizeInvoiceUnit(quantityMatch[1]);
    const rawName = cleanDanfeProductName(`${pendingDescription} ${line}`
      .replace(/\b(CX|UN|UM|UND|DZ|FD|KG|G|LT|L|ML|PC|PCT|CJ|SC|FR|GF|GAR|BDJ)\b/gi, ' ')
      .replace(/\b\d+(?:[,.]\d{1,4})?\b/g, ' ')
      .replace(/[^A-Za-zÀ-ÿ0-9/ -]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim());
    pendingDescription = '';
    if (rawName.length < 4 || /^(?:dados|valor|base|produto|servi)/i.test(rawName)) return;

    const afterQuantity = line.slice((quantityMatch.index || 0) + quantityMatch[0].length);
    const decimalTokens = Array.from(afterQuantity.matchAll(/\d+[,.]\d{2,4}/g)).map(match => ({ raw: match[0], value: parseBrazilianOcrNumber(match[0]), decimals: (match[0].split(',')[1] || '').length }));
    const unitPriceToken = decimalTokens.find(token => token.decimals >= 3 && token.value > 0);
    const unitPrice = unitPriceToken?.value || 0;
    const expectedTotal = unitPrice > 0 ? unitPrice * quantity : 0;
    const totalToken = decimalTokens.find(token => token.decimals === 2 && (!expectedTotal || Math.abs(token.value - expectedTotal) < 0.2))
      || decimalTokens.at(-1);
    const total = totalToken?.value || expectedTotal;
    const supplierProductCode = `DANFE-${index + 1}`;
    if (items.some(item => item.supplierProductName.toLowerCase() === rawName.toLowerCase())) return;

    items.push({
      supplierProductCode,
      supplierProductName: rawName,
      supplierBarcode: '',
      supplierNcm: '',
      supplierCfop: '',
      unit,
      quantity,
      unitPrice,
      total,
      productId: mappings.find(mapping => mapping.supplierProductName?.toLowerCase() === rawName.toLowerCase())?.productId || guessProductId(rawName, products)
    });
  });

  return items;
};

const parseInvoicePhotoText = (text: string, products: Product[], mappings: InvoiceProductMapping[]): { items: InvoiceImportItem[]; supplier: InvoiceSupplier | null; declaredTotal: number } => {
  const lines = text.split(/\r?\n/).map(line => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
  const isDanfePhoto = /\bDANFE\b|DANFE_PRODUCT_CROP_START|DANFE_DESCRIPTION_COLUMN_START/i.test(text);
  const danfeItems = isDanfePhoto ? parseDanfePhotoItems(text, products, mappings) : [];
  const items: InvoiceImportItem[] = [...danfeItems];
  const ignoredLine = /(?:total\s+(?:da\s+)?nota|valor\s+(?:total|a\s+pagar)|c[aá]lculo\s+do\s+imposto|dados\s+(?:adicionais|do\s+emitente)|natureza\s+da\s+opera[cç][aã]o)/i;
  const moneyPattern = /(?:R\$\s*)?(?:\d{1,3}(?:\.\d{3})*|\d+),\d{2}/g;
  const unitPattern = /\b(CX|UN|UND|DZ|FD|KG|G|LT|L|ML|PC|PCT|CJ|SC|FR|GF|GAR|BDJ)\b/i;

  if (!isDanfePhoto) lines.forEach((line, index) => {
    if (ignoredLine.test(line) || !/[A-Za-zÀ-ÿ]{3,}/.test(line)) return;
    const moneyValues = Array.from(line.matchAll(moneyPattern)).map(match => parseBrazilianOcrNumber(match[0]));
    if (!moneyValues.length) return;

    const codeMatch = line.match(/^(\d{3,14})\b/);
    const barcode = line.match(/\b\d{8,14}\b/)?.[0] || '';
    const numericSource = codeMatch ? line.slice(codeMatch[0].length) : line;
    const numericValues = Array.from(numericSource.matchAll(/(?<![\d.,])\d+(?:[,.]\d{1,4})?(?![\d.,])/g)).map(match => match[0]);
    const quantityMatch = numericSource.match(/(?<![\d.,])(\d+(?:[,.]\d{1,4})?)\s*(CX|UN|UND|DZ|FD|KG|G|LT|PC|PCT|CJ|SC|FR|GF|GAR|BDJ)\b/i);
    const leadingQuantityMatch = numericSource.match(/^\s*(\d+(?:[,.]\d{1,4})?)\s*x\b/i);
    const quantityValue = quantityMatch?.[1] || leadingQuantityMatch?.[1] || '';
    const unit = quantityMatch?.[2]?.toUpperCase() || unitPattern.exec(line)?.[1]?.toUpperCase() || 'UN';
    const quantity = quantityValue
      ? Math.max(1, parseBrazilianOcrNumber(quantityValue))
      : codeMatch && numericValues.length
        ? Math.max(1, parseBrazilianOcrNumber(numericValues[0]))
        : 1;
    const total = moneyValues.at(-1) || 0;
    const unitPrice = moneyValues.length > 1 ? moneyValues.at(-2) || total : total;
    const name = line
      .replace(/^\d{3,14}\b/, '')
      .replace(/\b\d{8,14}\b/g, '')
      .replace(moneyPattern, '')
      .replace(/\b\d+(?:[,.]\d{1,4})?\b/g, '')
      .replace(unitPattern, '')
      .replace(/[|:;]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (name.length < 3) return;

    const supplierProductCode = codeMatch?.[1] || barcode || `FOTO-${index + 1}`;
    if (items.some(item => item.supplierProductCode === supplierProductCode)) return;
    items.push({
      supplierProductCode,
      supplierProductName: name,
      supplierBarcode: barcode,
      supplierNcm: '',
      supplierCfop: '',
      unit,
      quantity,
      unitPrice,
      total,
      productId: mappings.find(mapping => mapping.supplierProductCode === supplierProductCode)?.productId || guessProductId(name, products)
    });
  });

  const supplierCropStart = text.lastIndexOf('DANFE_SUPPLIER_CROP_START');
  const supplierLines = (supplierCropStart >= 0 ? text.slice(supplierCropStart) : text)
    .split(/\r?\n/)
    .map(line => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const joinedText = supplierLines.join(' ');
  const supplierLine = supplierLines.find(line => /(?:RAZ[AÃA]O\s+SOCIAL|NOME\s+EMPRESARIAL|EMITENTE)/i.test(line)) || '';
  const nameMatch = supplierLine.match(/(?:RAZ[AÃA]O\s+SOCIAL|NOME\s+EMPRESARIAL|EMITENTE)\s*[:-]?\s*(.+?)(?=\s+(?:CNPJ|CPF)\b|$)/i);
  const brandedSupplierLine = supplierLines.find(line => /\b[A-ZÀ-Ý][A-ZÀ-Ý\s]{2,}\s+(?:S|H)\.A\.\s*-/i.test(line)) || '';
  const supplierName = (nameMatch?.[1]?.trim() || brandedSupplierLine.split('-')[0]?.trim() || '').replace(/\bH\.A\.$/i, 'S.A.');
  const supplier = supplierName
    ? {
        name: supplierName,
        document: joinedText.match(/(?:CNPJ|CPF)\s*[:-]?\s*([\d./-]{11,18})/i)?.[1] || '',
        email: joinedText.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || '',
        phone: joinedText.match(/(?<!\d)(?:\+?55\s*)?(?:\(\d{2}\)\s*|\d{2}[\s-])9?\d{4}[-\s]\d{4}(?!\d)/)?.[0] || ''
      }
    : null;
  const declaredTotalMatch = text.match(/(?:VALOR\s+DA\s+FATURA|VALOR\s+TOTAL\s+DA\s+NOTA)[^\d]{0,80}(\d{1,3},\d{2})/i);
  const declaredTotal = declaredTotalMatch ? parseBrazilianOcrNumber(declaredTotalMatch[1]) : 0;

  const safeItems = isDanfePhoto ? items.filter(isReliableDanfeItem) : items;
  return { items: safeItems.slice(0, 80), supplier, declaredTotal };
};

export const Stock = () => {
  const { user, products, currentCompany, refreshData, updateProductStock, addProductStockBatch, verifyManagerAuth } = useStore();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todas');
  const [stockFilter, setStockFilter] = useState<StockFilter>('ALL');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const [batchSearch, setBatchSearch] = useState('');
  const [batchEntries, setBatchEntries] = useState<Record<string, string>>({});
  const [batchError, setBatchError] = useState('');
  const [batchSuccess, setBatchSuccess] = useState('');
  const [isBatchSaving, setIsBatchSaving] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const tourStorageKey = `flowtab-stock-tour-v1:${user?.id}`;
  const tourSnapshotRef = useRef<{
    searchTerm: string; categoryFilter: string; stockFilter: StockFilter;
    isBatchOpen: boolean; isXmlGuideOpen: boolean;
    batchEntries: Record<string, string>; batchSearch: string;
  } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authPassword, setAuthPassword] = useState('');
  const [isXmlGuideOpen, setIsXmlGuideOpen] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceImportItem[]>([]);
  const [invoiceQuantityChoices, setInvoiceQuantityChoices] = useState<Record<string, InvoiceQuantityChoice>>({});
  const [invoiceDeclaredTotal, setInvoiceDeclaredTotal] = useState(0);
  const [invoiceMappings, setInvoiceMappings] = useState<InvoiceProductMapping[]>([]);
  const [invoiceSupplier, setInvoiceSupplier] = useState<InvoiceSupplier | null>(null);
  const [registerInvoiceSupplier, setRegisterInvoiceSupplier] = useState(false);
  const [xmlFileName, setXmlFileName] = useState('');
  const [invoiceKey, setInvoiceKey] = useState('');
  const [isImportingXml, setIsImportingXml] = useState(false);
  const [isReadingInvoicePhoto, setIsReadingInvoicePhoto] = useState(false);
  const [invoicePhotoUsage, setInvoicePhotoUsage] = useState<{ companyId: string; used: number; limit: number; remaining: number; globalRemaining: number } | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !currentCompany?.id) {
      setInvoicePhotoUsage(null);
      return;
    }

    const companyId = currentCompany.id;
    let active = true;
    setInvoicePhotoUsage(null);
    api.invoiceMappings.getPhotoOcrConfig(companyId).then(config => {
      if (active && config) setInvoicePhotoUsage({ companyId, ...config });
    }).catch(() => {});
    return () => { active = false; };
  }, [isAuthenticated, currentCompany?.id]);

  const stockTrackedProducts = useMemo(() => {
    return products.filter(p => p.trackStock).sort((a, b) => (a.stockQuantity ?? 0) - (b.stockQuantity ?? 0));
  }, [products]);
  const guideStockProducts = useMemo(() => stockTrackedProducts.length > 0 ? stockTrackedProducts : tourOpen ? [STOCK_TOUR_EXAMPLE] : [], [stockTrackedProducts, tourOpen]);
  const showTourExample = tourOpen && stockTrackedProducts.length === 0;
  const currentTourSteps = useMemo(() => stockTourSteps.map(step => showTourExample && step.target === 'stock-item'
    ? { ...step, detail: 'Este produto aparece só no guia e não será cadastrado nem alterará o estoque.' }
    : step), [showTourExample]);

  const startTour = useCallback(() => {
    tourSnapshotRef.current = { searchTerm, categoryFilter, stockFilter, isBatchOpen, isXmlGuideOpen, batchEntries, batchSearch };
    setSearchTerm('');
    setCategoryFilter('Todas');
    setStockFilter('ALL');
    setIsBatchOpen(false);
    setIsXmlGuideOpen(false);
    setBatchEntries({});
    setBatchSearch('');
    setTourStep(0);
    setTourOpen(true);
  }, [searchTerm, categoryFilter, stockFilter, isBatchOpen, isXmlGuideOpen, batchEntries, batchSearch]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !currentCompany?.id || isBatchOpen || isXmlGuideOpen || tourOpen) return;
    if (getActiveTourArea(user.id) === 'stock') {
      startTour();
      return;
    }
    let active = true;
    const checkTour = async () => {
      const status = await api.auth.getTutorialStatus('stock');
      if (!active) return;
      const seenLocally = window.localStorage.getItem(tourStorageKey) === 'seen';
      if (seenLocally && status && !status.completed) void api.auth.completeTutorial('stock');
      if (seenLocally || status?.completed) {
        window.localStorage.setItem(tourStorageKey, 'seen');
        return;
      }
      startTour();
    };
    void checkTour();
    return () => { active = false; };
  }, [isAuthenticated, user?.id, currentCompany?.id, isBatchOpen, isXmlGuideOpen, tourOpen, tourStorageKey, startTour]);

  const changeTourStep = (index: number) => {
    if (index >= 2 && index <= 4) {
      setIsBatchOpen(false);
      setIsXmlGuideOpen(true);
    } else if (index >= 11 && index <= 13) {
      setIsXmlGuideOpen(false);
      setIsBatchOpen(true);
      const exampleId = guideStockProducts[0]?.id;
      setBatchEntries(index >= 12 && exampleId ? { [exampleId]: '3' } : {});
    } else {
      setIsXmlGuideOpen(false);
      setIsBatchOpen(false);
    }
    setTourStep(index);
  };

  const finishTour = () => {
    window.localStorage.setItem(tourStorageKey, 'seen');
    void api.auth.completeTutorial('stock');
    const nextRoute = user ? advanceFirstAccessTour(user, 'stock') : null;
    const snapshot = tourSnapshotRef.current;
    if (snapshot) {
      setSearchTerm(snapshot.searchTerm);
      setCategoryFilter(snapshot.categoryFilter);
      setStockFilter(snapshot.stockFilter);
      setIsBatchOpen(snapshot.isBatchOpen);
      setIsXmlGuideOpen(snapshot.isXmlGuideOpen);
      setBatchEntries(snapshot.batchEntries);
      setBatchSearch(snapshot.batchSearch);
    } else {
      setIsBatchOpen(false);
      setIsXmlGuideOpen(false);
    }
    tourSnapshotRef.current = null;
    setTourOpen(false);
    setTourStep(0);
    if (nextRoute) navigate(nextRoute, { replace: true });
  };

  const categories = useMemo(() => {
    return ['Todas', ...Array.from(new Set(stockTrackedProducts.map(p => p.category)))];
  }, [stockTrackedProducts]);

  const filteredProducts = useMemo(() => {
    let result = stockTrackedProducts;
    if (searchTerm) {
      result = result.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (categoryFilter !== 'Todas') {
      result = result.filter(p => p.category === categoryFilter);
    }
    if (stockFilter === 'LOW') {
      result = result.filter(isLowStock);
    }
    if (stockFilter === 'OUT') {
      result = result.filter(isOutOfStock);
    }
    return result;
  }, [stockTrackedProducts, searchTerm, categoryFilter, stockFilter]);

  const batchProducts = useMemo(() => guideStockProducts
    .filter(product => product.name.toLocaleLowerCase('pt-BR').includes(batchSearch.trim().toLocaleLowerCase('pt-BR')))
    .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR')), [guideStockProducts, batchSearch]);
  const selectedBatchProducts = guideStockProducts.filter(product => Object.prototype.hasOwnProperty.call(batchEntries, product.id));
  const allVisibleBatchSelected = batchProducts.length > 0 && batchProducts.every(product => Object.prototype.hasOwnProperty.call(batchEntries, product.id));

  const totalUnits = Math.round((stockTrackedProducts.reduce((sum, product) => sum + Number(product.stockQuantity ?? 0), 0) + Number.EPSILON) * 1000) / 1000;
  const lowStockProducts = stockTrackedProducts.filter(isLowStock);
  const outOfStockProducts = stockTrackedProducts.filter(isOutOfStock);
  const lowStockCount = lowStockProducts.length;
  const outOfStockCount = outOfStockProducts.length;
  const preparedInvoiceItems = invoiceItems.map(item => convertInvoiceEntry(item, invoiceQuantityChoices[item.supplierProductCode]));
  const missingPackageSizes = invoiceItems.filter(item => invoiceQuantityChoices[item.supplierProductCode]?.mode === 'unit'
    && !isValidInvoicePackageSize(invoiceQuantityChoices[item.supplierProductCode].unitsPerPackage)).length;
  const linkedInvoiceItems = preparedInvoiceItems.filter(item => item.productId && item.quantity > 0);
  const missingInvoiceLinks = invoiceItems.filter(item => !item.productId).length;
  const invoiceTotalQuantity = preparedInvoiceItems.reduce((sum, item) => sum + item.quantity, 0);
  const invoiceItemsCost = preparedInvoiceItems.reduce((sum, item) => sum + (item.total || item.unitPrice * item.quantity), 0);
  const invoiceTotalCost = invoiceDeclaredTotal > 0 ? invoiceDeclaredTotal : invoiceItemsCost;
  const invoiceAmountDifference = invoiceDeclaredTotal - invoiceItemsCost;
  const invoicePhotoBadge = 'TEXTRACT';
  const invoicePhotoOcrPlanLabel = 'Leitura estruturada e conferência antes de importar. O limite mensal protege o orçamento.';
  const currentInvoicePhotoUsage = invoicePhotoUsage?.companyId === currentCompany?.id ? invoicePhotoUsage : null;
  const invoicePhotoProgress = currentInvoicePhotoUsage?.limit
    ? Math.min(100, Math.max(0, currentInvoicePhotoUsage.used / currentInvoicePhotoUsage.limit * 100))
    : 0;

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await verifyManagerAuth(authPassword)) {
      setIsAuthenticated(true);
    } else {
      alert('Senha incorreta. Acesso negado.');
      setAuthPassword('');
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    setIsRefreshing(false);
  };

  const handleUpdate = async (id: string, quantity: number, lowStockThreshold: number) => {
    if (tourOpen) return;
    await updateProductStock(id, quantity, lowStockThreshold);
  };

  const openBatchEntry = () => {
    setBatchSearch('');
    setBatchEntries({});
    setBatchError('');
    setBatchSuccess('');
    setIsBatchOpen(true);
  };

  const toggleBatchProduct = (productId: string) => {
    setBatchError('');
    setBatchEntries(previous => {
      const next = { ...previous };
      if (Object.prototype.hasOwnProperty.call(next, productId)) delete next[productId];
      else next[productId] = '';
      return next;
    });
  };

  const toggleVisibleBatchProducts = () => {
    setBatchError('');
    setBatchEntries(previous => {
      const next = { ...previous };
      for (const product of batchProducts) {
        if (allVisibleBatchSelected) delete next[product.id];
        else if (!Object.prototype.hasOwnProperty.call(next, product.id)) next[product.id] = '';
      }
      return next;
    });
  };

  const handleBatchEntry = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isBatchSaving || tourOpen) return;
    if (selectedBatchProducts.length === 0 || selectedBatchProducts.length > 100) {
      setBatchError('Selecione de 1 a 100 produtos.');
      return;
    }
    const items: { productId: string; quantity: number }[] = [];
    for (const product of selectedBatchProducts) {
      const quantity = parseManualStockQuantity(batchEntries[product.id], product.stockUnit);
      if (quantity === null) {
        setBatchError(`Informe uma quantidade válida para ${product.name}${(product.stockUnit || 'UN') === 'UN' ? ' (unidades inteiras)' : ''}.`);
        return;
      }
      items.push({ productId: product.id, quantity });
    }
    setBatchError('');
    setIsBatchSaving(true);
    try {
      const success = await addProductStockBatch(items);
      if (!success) {
        setBatchError('Não foi possível registrar a entrada. Nenhuma quantidade foi alterada. Tente novamente.');
        return;
      }
      setBatchSuccess(`Entrada registrada para ${items.length} ${items.length === 1 ? 'produto' : 'produtos'}. As quantidades foram somadas ao saldo anterior.`);
      setIsBatchOpen(false);
      setBatchEntries({});
    } catch {
      setBatchError('Não foi possível registrar a entrada. Tente novamente.');
    } finally {
      setIsBatchSaving(false);
    }
  };

  const handlePrintStockAlerts = () => {
    const alertRows = [...outOfStockProducts, ...lowStockProducts];
    const rows = alertRows.filter(product => !product.stockPackageProductId && !product.stockProductId);

    if (rows.length === 0) {
      alert(alertRows.length > 0
        ? 'Os itens em alerta são derivados de outro estoque. A reposição deve ser feita pelo item principal/embalagem.'
        : 'Nenhum item zerado ou acabando para imprimir.'
      );
      return;
    }

    const now = new Date().toLocaleString('pt-BR');
    const htmlRows = rows.map(product => {
      const quantity = Number(product.stockQuantity ?? 0);
      const threshold = getLowStockThreshold(product);
      const status = quantity <= 0 ? 'ZERADO' : 'ACABANDO';
      const statusClass = quantity <= 0 ? 'out' : 'low';
      return `
        <div class="item">
          <div class="item-name">${escapeHtml(product.name)}</div>
          <div class="item-category">${escapeHtml(product.category)}</div>
          <div class="item-info">
            <span>Atual: <strong>${quantity}</strong></span>
            <span>Mínimo: <strong>${threshold}</strong></span>
            <span class="status ${statusClass}">${status}</span>
          </div>
        </div>
      `;
    }).join('');

    const printWindow = window.open('', '_blank', 'width=360,height=700');
    if (!printWindow) return;
    const companyName = escapeHtml(currentCompany?.name || 'FlowTab');

    printWindow.document.write(`
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Lista de reposição - FlowTab</title>
          <style>
            @page { size: 75mm auto; margin: 0; }
            * { box-sizing: border-box; }
            body { width: 75mm; margin: 0; padding: 4mm; color: #000; font-family: 'Courier New', monospace; font-size: 10px; line-height: 1.25; }
            .center { text-align: center; }
            h1 { margin: 0; font-size: 12px; font-weight: 900; text-transform: uppercase; }
            .subtitle { margin: 2mm 0 3mm; font-size: 9px; font-weight: 700; }
            .dash { border-top: 1px dashed #000; margin: 3mm 0; }
            .list { display: block; }
            .item { padding: 2.2mm 0; border-bottom: 1px dotted #888; page-break-inside: avoid; }
            .item-name { font-size: 10.5px; font-weight: 900; text-transform: uppercase; overflow-wrap: anywhere; word-break: break-word; }
            .item-category { font-size: 8px; margin-top: .6mm; opacity: .8; text-transform: uppercase; overflow-wrap: anywhere; }
            .item-info { display: flex; justify-content: space-between; align-items: center; gap: 2mm; margin-top: 1.3mm; font-size: 9px; }
            .item-info span { white-space: nowrap; }
            .status { font-weight: 900; padding-left: 1mm; }
            .status.out { color: #000; }
            .status.low { color: #333; }
            .legend { display: flex; justify-content: space-between; font-size: 8px; font-weight: 900; text-transform: uppercase; margin-bottom: 1mm; }
            .footer { text-align: center; margin-top: 5mm; }
            .footer img { width: 36mm; opacity: .85; display: block; margin: 0 auto 1mm; transform: translateX(-4mm); }
            .footer p { margin: 0; font-size: 8px; }
          </style>
        </head>
        <body>
          <div class="center">
            <h1>${companyName}</h1>
            <div class="subtitle">LISTA DE REPOSIÇÃO</div>
            <div>Emitido em: ${now}</div>
          </div>
          <div class="dash"></div>
          <div class="legend"><span>Produto</span><span>Estoque</span></div>
          <div class="list">${htmlRows}</div>
          <div class="dash"></div>
          <div class="footer">
            <img src="/flowtab-logo-dark-transparent.png" alt="FlowTab" />
            <p>FlowTab - controle de comandas</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const openXmlImporter = async () => {
    setIsXmlGuideOpen(true);
    setInvoiceItems([]);
    setInvoiceDeclaredTotal(0);
    setXmlFileName('');
    setInvoiceSupplier(null);
    setRegisterInvoiceSupplier(false);
    setInvoiceKey('');

    if (currentCompany?.id) {
      const mappings = await api.invoiceMappings.getByCompany(currentCompany.id);
      setInvoiceMappings(mappings || []);
    }
  };

  const handleXmlFile = async (file?: File) => {
    if (!file) return;

    try {
      const xmlText = await file.text();
      const parsedResult = parseInvoiceXml(xmlText, stockTrackedProducts, invoiceMappings);
      const parsedItems = parsedResult.items;

      if (parsedItems.length === 0) {
        alert('Não encontrei produtos nesse XML. Confirme se ele é o XML completo da NF-e.');
        return;
      }

      setXmlFileName(file.name);
      setInvoiceItems(parsedItems);
      setInvoiceQuantityChoices({});
      setInvoiceDeclaredTotal(0);
      setInvoiceSupplier(parseInvoiceSupplier(xmlText));
      setInvoiceKey(parsedResult.invoiceKey || await createInvoiceFileKey(file));
    } catch (error: any) {
      alert(error?.message || 'Não foi possível ler o XML da nota.');
    }
  };

  const handleInvoicePhotoFile = async (file?: File) => {
    if (!file) return;
    if (!currentCompany?.id) return;

    try {
      setIsReadingInvoicePhoto(true);
      const mappings = invoiceMappings.length
        ? invoiceMappings
        : currentCompany?.id ? await api.invoiceMappings.getByCompany(currentCompany.id) || [] : [];
      setInvoiceMappings(mappings);

      const response = await api.invoiceMappings.readPhotoOcr(currentCompany.id, {
        content: await readFileAsBase64(file),
        mimeType: file.type || 'image/jpeg'
      });
      if (response && !response.success) {
        alert(response.error || 'Não foi possível ler a foto da nota.');
        return;
      }
      const textFallback = response?.items.length
        ? null
        : parseInvoicePhotoText(response?.text || '', stockTrackedProducts, mappings);
      const importedItems = response?.items.length ? response.items : textFallback?.items || [];
      if (!response?.success || !importedItems.length) {
        alert('Não encontrei itens nessa foto. Tente uma imagem mais nítida ou use o XML da NF-e.');
        return;
      }

      setXmlFileName(`Foto: ${file.name}`);
      setInvoiceItems(importedItems);
      setInvoiceQuantityChoices({});
      setInvoiceDeclaredTotal(Number(response.declaredTotal || textFallback?.declaredTotal || 0));
      setInvoiceSupplier(response.supplier || textFallback?.supplier || null);
      setInvoiceKey(await createInvoiceFileKey(file));
      setRegisterInvoiceSupplier(false);
      setIsXmlGuideOpen(true);
    } catch (error: any) {
      alert(error?.message || 'Não foi possível ler a foto da nota.');
    } finally {
      setIsReadingInvoicePhoto(false);
      const usage = await api.invoiceMappings.getPhotoOcrConfig(currentCompany.id).catch(() => null);
      if (usage) setInvoicePhotoUsage({ companyId: currentCompany.id, ...usage });
    }
  };

  const updateInvoiceItemProduct = (supplierProductCode: string, productId: string) => {
    setInvoiceItems(prev => prev.map(item => (
      item.supplierProductCode === supplierProductCode ? { ...item, productId } : item
    )));
  };

  const updateInvoiceItemPrice = (supplierProductCode: string, field: 'unitPrice' | 'total', value: string) => {
    const parsedValue = Math.max(0, Number(value.replace(',', '.') || 0));
    setInvoiceItems(prev => prev.map(item => (
      item.supplierProductCode === supplierProductCode
        ? { ...item, [field]: field === 'unitPrice' && invoiceQuantityChoices[supplierProductCode]?.mode === 'unit'
          ? parsedValue * invoiceQuantityChoices[supplierProductCode].unitsPerPackage : parsedValue }
        : item
    )));
  };

  const updateInvoiceQuantityChoice = (supplierProductCode: string, choice: InvoiceQuantityChoice) => {
    setInvoiceQuantityChoices(prev => ({ ...prev, [supplierProductCode]: choice }));
  };

  const handleConfirmInvoiceImport = async () => {
    if (tourOpen) return;
    if (!currentCompany?.id) {
      alert('Empresa não encontrada. Faça login novamente.');
      return;
    }

    if (invoiceItems.length === 0) {
      alert('Selecione um XML primeiro.');
      return;
    }

    if (missingInvoiceLinks > 0) {
      alert('Vincule todos os itens da nota a um produto do estoque antes de confirmar.');
      return;
    }

    if (missingPackageSizes > 0) {
      alert('Informe quantas unidades existem em cada embalagem selecionada.');
      return;
    }

    setIsImportingXml(true);
    const result = await api.invoiceMappings.importStock(currentCompany.id, linkedInvoiceItems.map(item => ({
      supplierProductCode: item.supplierProductCode,
      supplierProductName: item.supplierProductName,
      supplierBarcode: item.supplierBarcode,
      supplierNcm: item.supplierNcm,
      supplierCfop: item.supplierCfop,
      supplierUnit: item.unit,
      unitPrice: item.unitPrice,
      total: item.total,
      quantity: item.quantity,
      productId: item.productId
    })), registerInvoiceSupplier && invoiceSupplier?.name ? invoiceSupplier : undefined, invoiceKey, invoiceDeclaredTotal);

    if (result?.code === 'INVOICE_ALREADY_IMPORTED') {
      setIsImportingXml(false);
      alert(result.error || 'Esta nota já foi importada neste mês. O estoque e os gastos não foram alterados.');
      return;
    }

    if (!result || !result.updatedProducts || !result.mappings) {
      setIsImportingXml(false);
      alert('Não consegui importar a nota agora. Tente novamente.');
      return;
    }

    await refreshData();
    setInvoiceMappings(result.mappings || []);
    setInvoiceItems([]);
    setInvoiceQuantityChoices({});
    setInvoiceDeclaredTotal(0);
    setXmlFileName('');
    setInvoiceKey('');
    setInvoiceSupplier(null);
    setRegisterInvoiceSupplier(false);
    setIsImportingXml(false);
    setIsXmlGuideOpen(false);
    alert(`Nota importada com sucesso. O estoque foi atualizado${result.expense ? ' e o valor foi lançado nos gastos' : ''}.`);
  };

  if (!isAuthenticated) {
    return <ProtectedAreaGate title="Acesso ao estoque" description={user && getActiveTourArea(user.id) === 'stock' ? 'Confirme a senha autorizada para continuar o tour completo. O guia de Estoque abrirá automaticamente.' : 'Insira sua senha de dono ou de um gerente autorizado para visualizar e modificar produtos, quantidades e entradas.'} password={authPassword} onPasswordChange={setAuthPassword} onSubmit={handleAuth} onBack={() => { if (user && getActiveTourArea(user.id) === 'stock') { stopFirstAccessTour(user.id); navigate('/pos'); } else navigate(-1); }} submitLabel="Acessar estoque" accent="teal" />;
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-50 font-sans">
      <div className="flex-1 overflow-y-auto p-4 md:p-6 no-scrollbar">
        <div className="max-w-5xl mx-auto pb-20">
          <header data-tour="stock-overview" className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white rounded-[2rem] p-6 md:p-8 shadow-xl shadow-slate-200 mb-6 overflow-hidden relative">
            <div className="absolute -right-16 -top-16 w-48 h-48 bg-brand-500/20 rounded-full blur-3xl"></div>
            <div className="absolute right-10 bottom-0 w-28 h-28 bg-cyan-400/10 rounded-full blur-2xl"></div>
            <div className="relative flex flex-col lg:flex-row justify-between gap-6">
              <div>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 text-xs font-bold text-cyan-100"><Sparkles className="w-3.5 h-3.5" /> Controle inteligente</div>
                  <button type="button" onClick={startTour} aria-label="Abrir guia do Estoque" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-xs font-black text-emerald-100 hover:bg-emerald-400/20"><CircleHelp className="h-3.5 w-3.5" /> Guia do Estoque</button>
                </div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight">Gestão de Estoque</h1>
                <p className="text-slate-300 text-sm md:text-base mt-2 max-w-xl">Acompanhe produtos baixos, atualize quantidades e prepare importação por XML de nota fiscal sem custo extra.</p>
              </div>
              <div className="grid grid-cols-3 gap-2 min-w-full lg:min-w-[360px]">
                <div className="bg-white/10 border border-white/10 rounded-2xl p-4">
                  <p className="text-[10px] uppercase font-black text-slate-300">Itens</p>
                  <p className="text-2xl font-black">{stockTrackedProducts.length}</p>
                </div>
                <div className="bg-white/10 border border-white/10 rounded-2xl p-4">
                  <p className="text-[10px] uppercase font-black text-slate-300">Unidades</p>
                  <p className="text-2xl font-black">{Math.round(totalUnits)}</p>
                </div>
                <div className="bg-white/10 border border-white/10 rounded-2xl p-4">
                  <p className="text-[10px] uppercase font-black text-slate-300">Alertas</p>
                  <p className="text-2xl font-black text-amber-200">{lowStockCount + outOfStockCount}</p>
                </div>
              </div>
            </div>
          </header>

          {batchSuccess && (
            <div role="status" className="mb-5 flex items-start justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
              <span className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0" />{batchSuccess}</span>
              <button type="button" onClick={() => setBatchSuccess('')} aria-label="Fechar aviso"><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="grid lg:grid-cols-[1fr_340px] gap-4 mb-6 items-start">
            <div className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-black text-brand-600">Nota fiscal e compras</p>
                    <h2 className="font-black text-slate-900 text-xl">Entrada inteligente de estoque</h2>
                    <p className="text-sm text-slate-500 mt-1">O XML gratuito já puxa código, EAN, NCM, CFOP, unidade, preço, total e quantidade. A foto usa o Textract, com limite mensal e conferência antes de importar.</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <button
                  data-tour="stock-xml-card"
                  onClick={openXmlImporter}
                  className="text-left rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 hover:border-emerald-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-white text-emerald-600 flex items-center justify-center shadow-sm">
                      <UploadCloud className="w-5 h-5" />
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-[10px] font-black text-emerald-700">SEM CUSTO EXTRA</span>
                  </div>
                  <p className="font-black text-slate-900 mt-3">Importar XML da NF-e</p>
                  <p className="text-xs text-slate-500 mt-1">Melhor opção para todos os planos: confere antes, soma estoque e salva vínculo por código.</p>
                </button>

                <label data-tour="stock-photo-card" className="rounded-2xl border p-4 transition-all bg-slate-950 text-white border-slate-800 cursor-pointer hover:border-cyan-300 hover:shadow-lg">
                  <div className="flex items-center justify-between gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-white/10 text-cyan-200">
                      {isReadingInvoicePhoto ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                    </div>
                    <span className="rounded-full px-3 py-1 text-[10px] font-black bg-cyan-400/10 text-cyan-200">{invoicePhotoBadge}</span>
                  </div>
                  <p className="font-black mt-3">{isReadingInvoicePhoto ? 'Lendo nota...' : 'Foto da nota com IA'}</p>
                  <p className="text-xs mt-1 text-slate-300">{invoicePhotoOcrPlanLabel}</p>
                  {currentInvoicePhotoUsage && currentInvoicePhotoUsage.limit > 0 && (
                    <div className="mt-4 rounded-xl bg-white/10 p-3">
                      <div className="flex items-center justify-between gap-2 text-[11px] font-bold">
                        <span className="uppercase tracking-wide text-slate-300">Uso do mês</span>
                        <span>{currentInvoicePhotoUsage.used}/{currentInvoicePhotoUsage.limit} leituras</span>
                      </div>
                      <div
                        role="progressbar"
                        aria-label="Leituras de notas por foto utilizadas neste mês"
                        aria-valuemin={0}
                        aria-valuemax={currentInvoicePhotoUsage.limit}
                        aria-valuenow={Math.min(currentInvoicePhotoUsage.used, currentInvoicePhotoUsage.limit)}
                        className="mt-2 h-2 overflow-hidden rounded-full bg-white/15"
                      >
                        <div className="h-full rounded-full bg-cyan-300 transition-all duration-300" style={{ width: `${invoicePhotoProgress}%` }} />
                      </div>
                      <p className="mt-2 text-[11px] text-slate-300">
                        {currentInvoicePhotoUsage.globalRemaining === 0
                          ? 'Limite geral de leituras atingido neste mês.'
                          : `${currentInvoicePhotoUsage.remaining} ${currentInvoicePhotoUsage.remaining === 1 ? 'leitura restante' : 'leituras restantes'}.`}
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/tiff,application/pdf"
                    disabled={isReadingInvoicePhoto}
                    onChange={(event) => {
                      handleInvoicePhotoFile(event.target.files?.[0]);
                      event.currentTarget.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                data-tour="stock-low-filter"
                type="button"
                onClick={() => setStockFilter(stockFilter === 'LOW' ? 'ALL' : 'LOW')}
                className={`group text-left rounded-[1.5rem] border shadow-sm p-4 transition-all overflow-hidden relative min-h-[132px] ${stockFilter === 'LOW' ? 'bg-amber-50 border-amber-300 ring-4 ring-amber-100' : 'bg-white border-slate-200 hover:border-amber-200 hover:shadow-md'}`}
              >
                <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-amber-100/70 transition-transform group-hover:scale-110"></div>
                <div className="relative flex h-full flex-col justify-between gap-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-white/80 px-2.5 py-1 text-[10px] font-black uppercase text-amber-700">{stockFilter === 'LOW' ? 'Aberto' : 'Ver'}</span>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-400">Itens acabando</p>
                    <p className="text-4xl font-black text-amber-500 leading-none mt-1">{lowStockCount}</p>
                  </div>
                </div>
              </button>
              <button
                data-tour="stock-out-filter"
                type="button"
                onClick={() => setStockFilter(stockFilter === 'OUT' ? 'ALL' : 'OUT')}
                className={`group text-left rounded-[1.5rem] border shadow-sm p-4 transition-all overflow-hidden relative min-h-[132px] ${stockFilter === 'OUT' ? 'bg-red-50 border-red-300 ring-4 ring-red-100' : 'bg-white border-slate-200 hover:border-red-200 hover:shadow-md'}`}
              >
                <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-red-100/70 transition-transform group-hover:scale-110"></div>
                <div className="relative flex h-full flex-col justify-between gap-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-100 text-red-600">
                      <CircleSlash className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-white/80 px-2.5 py-1 text-[10px] font-black uppercase text-red-700">{stockFilter === 'OUT' ? 'Aberto' : 'Ver'}</span>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-400">Itens zerados</p>
                    <p className="text-4xl font-black text-red-500 leading-none mt-1">{outOfStockCount}</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div className="sticky top-0 bg-slate-50/90 backdrop-blur-sm py-4 z-10 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div data-tour="stock-search" className="bg-white p-1 rounded-2xl shadow-sm border border-slate-200 flex items-center flex-1">
                <div className="p-3"><Search className="w-4 h-4 text-slate-400" /></div>
                <input
                  type="text"
                  placeholder="Filtrar por nome..."
                  className="flex-1 outline-none text-slate-700 p-2 text-sm bg-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button data-tour="stock-refresh" onClick={handleRefresh} variant="secondary" size="sm" className="rounded-2xl px-5 h-12 bg-white">
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} /> Atualizar
              </Button>
              <Button data-tour="stock-batch-button" onClick={openBatchEntry} disabled={stockTrackedProducts.length === 0 && !tourOpen} variant="success" size="sm" className="rounded-2xl px-5 h-12 whitespace-nowrap disabled:opacity-50">
                <Plus className="w-4 h-4" /> Entrada manual em lote
              </Button>
              <Button data-tour="stock-print" onClick={handlePrintStockAlerts} variant="secondary" size="sm" className="rounded-2xl px-5 h-12 bg-white">
                <Printer className="w-4 h-4" /> Imprimir faltas
              </Button>
            </div>

            {stockFilter !== 'ALL' && (
              <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                <p className="text-sm font-bold text-slate-600">
                  Mostrando {stockFilter === 'LOW' ? 'itens acabando' : 'itens zerados'}
                </p>
                <button type="button" onClick={() => setStockFilter('ALL')} className="text-xs font-black text-brand-600 hover:text-brand-700">Ver todos</button>
              </div>
            )}

            <div data-tour="stock-categories" className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${categoryFilter === cat ? 'bg-brand-600 text-white shadow-md shadow-brand-100' : 'bg-white border border-slate-200 text-slate-600'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3 mt-4">
            {showTourExample ? (
              <StockItem product={STOCK_TOUR_EXAMPLE} onUpdate={handleUpdate} tourTarget />
            ) : filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <StockItem key={product.id} product={product} onUpdate={handleUpdate} tourTarget={index === 0} />
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-[1.75rem] border-2 border-dashed border-slate-200">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-600 font-black">Nenhum item encontrado.</p>
                <p className="text-sm text-slate-400">Ative o controle de estoque para produtos no Cardápio.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isBatchOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:p-5">
          <div role="dialog" aria-modal="true" aria-labelledby="batch-entry-title" className="flex max-h-[94dvh] w-full max-w-3xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 bg-slate-950 px-5 py-5 text-white sm:px-7">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300">Reposição de estoque</p>
                <h2 id="batch-entry-title" className="mt-1 text-xl font-black sm:text-2xl">Entrada manual em lote</h2>
                <p className="mt-2 max-w-xl text-xs leading-5 text-slate-300">Selecione os produtos e informe quanto chegou. O valor digitado será <strong className="text-white">somado</strong> ao estoque atual, não substituirá o saldo.</p>
              </div>
              <button type="button" onClick={() => setIsBatchOpen(false)} disabled={isBatchSaving} aria-label="Fechar entrada manual" className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-50"><X className="h-5 w-5" /></button>
            </div>

            <form onSubmit={handleBatchEntry} className="flex min-h-0 flex-1 flex-col">
              <div className="border-b border-slate-100 px-5 py-4 sm:px-7">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <label className="flex flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input type="search" autoFocus value={batchSearch} onChange={event => setBatchSearch(event.target.value)} placeholder="Buscar produto..." aria-label="Buscar produtos para entrada" className="w-full bg-transparent text-sm font-semibold text-slate-800 outline-none" />
                  </label>
                  <button type="button" onClick={toggleVisibleBatchProducts} disabled={batchProducts.length === 0} className="rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    {allVisibleBatchSelected ? 'Desmarcar visíveis' : 'Selecionar visíveis'}
                  </button>
                </div>
                <p className="mt-2 text-xs font-bold text-slate-500">{selectedBatchProducts.length} {selectedBatchProducts.length === 1 ? 'produto selecionado' : 'produtos selecionados'} · até 100 por entrada</p>
              </div>

              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-slate-50 p-4 sm:px-7">
                {batchProducts.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">Nenhum produto encontrado.</p> : batchProducts.map(product => {
                  const selected = Object.prototype.hasOwnProperty.call(batchEntries, product.id);
                  const quantity = selected ? parseManualStockQuantity(batchEntries[product.id], product.stockUnit) : null;
                  const unit = STOCK_UNIT_LABELS[product.stockUnit || 'UN'];
                  return (
                    <div key={product.id} data-tour={product.id === guideStockProducts[0]?.id ? 'stock-batch-list' : undefined} className={`rounded-2xl border bg-white p-3 sm:p-4 ${selected ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-slate-200'}`}>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" checked={selected} onChange={() => toggleBatchProduct(product.id)} aria-label={`Selecionar ${product.name}`} className="h-5 w-5 shrink-0 accent-emerald-600" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-black text-slate-900">{product.name}</p>
                          <p className="text-[11px] font-semibold text-slate-500">{product.category} · Atual: {formatStockQuantity(Number(product.stockQuantity || 0))} {unit}</p>
                        </div>
                        {selected && <span className={`hidden whitespace-nowrap text-xs font-black sm:block ${quantity === null ? 'text-slate-400' : 'text-emerald-700'}`}>{quantity === null ? 'Informe uma quantidade válida' : `Após entrada: ${formatStockQuantity(Number(product.stockQuantity || 0) + quantity)} ${unit}`}</span>}
                      </div>
                      {selected && (
                        <label data-tour={product.id === guideStockProducts[0]?.id ? 'stock-batch-quantity' : undefined} className="mt-3 flex items-center gap-3 border-t border-slate-100 pt-3">
                          <span className="shrink-0 text-xs font-black text-slate-600">Adicionar</span>
                          <input type="text" inputMode={(product.stockUnit || 'UN') === 'UN' ? 'numeric' : 'decimal'} value={batchEntries[product.id]} onChange={event => setBatchEntries(previous => ({ ...previous, [product.id]: event.target.value }))} placeholder={(product.stockUnit || 'UN') === 'UN' ? 'Ex.: 12' : 'Ex.: 2,5'} aria-label={`Quantidade a adicionar para ${product.name}`} className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-black text-slate-900 outline-none focus:border-emerald-500" />
                          <span className="shrink-0 text-xs font-black text-slate-500">{unit}</span>
                          <span className="text-xs font-bold text-emerald-700 sm:hidden">{quantity === null ? '—' : `→ ${formatStockQuantity(Number(product.stockQuantity || 0) + quantity)}`}</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-100 bg-white px-5 py-4 sm:px-7">
                {batchError && <p role="alert" className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-700">{batchError}</p>}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" variant="secondary" onClick={() => setIsBatchOpen(false)} disabled={isBatchSaving} className="h-11 rounded-xl">Cancelar</Button>
                  <Button data-tour="stock-batch-confirm" type="submit" variant="success" disabled={isBatchSaving || tourOpen || selectedBatchProducts.length === 0} className="h-11 rounded-xl disabled:opacity-50"><Plus className="h-4 w-4" />{isBatchSaving ? 'Somando...' : `Somar ao estoque (${selectedBatchProducts.length})`}</Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {isXmlGuideOpen && (
        <div className="fixed inset-0 z-[200] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="bg-gradient-to-br from-slate-950 to-slate-800 text-white p-6 relative overflow-hidden flex-shrink-0">
              <div className="absolute -right-10 -top-10 w-36 h-36 rounded-full bg-brand-500/20 blur-2xl"></div>
              <button onClick={() => setIsXmlGuideOpen(false)} className="absolute right-5 top-5 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
              <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-cyan-200" />
              </div>
              <h2 className="text-2xl font-black">{xmlFileName.startsWith('Foto:') ? 'Conferir nota por foto' : 'Importar XML da Nota Fiscal'}</h2>
              <p className="text-slate-300 text-sm mt-2 max-w-2xl">Confira os produtos encontrados e diga uma vez qual item da nota corresponde ao produto do seu estoque. Nas próximas notas com o mesmo código, o FlowTab já preenche sozinho.</p>
            </div>

            <div className="p-5 md:p-6 overflow-y-auto space-y-5">
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  ['1', xmlFileName.startsWith('Foto:') ? 'Ler foto' : 'Ler XML', xmlFileName.startsWith('Foto:') ? 'O sistema sugere os dados da imagem para sua conferência.' : 'O sistema pega código, nome e quantidade direto da NF-e.'],
                  ['2', 'Vincular produto', 'Você escolhe qual item do estoque cada código da nota alimenta.'],
                  ['3', 'Confirmar entrada', 'Só depois da conferência o estoque é somado e o vínculo é salvo.']
                ].map(([step, title, description]) => (
                  <div key={step} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center font-black text-sm mb-3">{step}</div>
                    <h3 className="font-black text-slate-900 text-sm">{title}</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{description}</p>
                  </div>
                ))}
              </div>

              <label data-tour="stock-xml-upload" className="block border-2 border-dashed border-slate-200 hover:border-brand-300 bg-slate-50 hover:bg-brand-50/40 rounded-[1.5rem] p-5 text-center cursor-pointer transition-colors">
                <UploadCloud className="w-10 h-10 mx-auto text-brand-600 mb-3" />
                <p className="font-black text-slate-900">Selecionar XML da nota</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Aceita arquivo .xml da NF-e. Nada é lançado sem conferência.</p>
                {xmlFileName && <p className="text-xs font-black text-brand-700 mt-3">Arquivo: {xmlFileName}</p>}
                <input
                  type="file"
                  accept=".xml,text/xml,application/xml"
                  className="hidden"
                  onChange={(event) => {
                    handleXmlFile(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
              </label>

              {tourOpen && <div data-tour="stock-xml-review" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Prévia do guia · não é uma nota real</p>
                <div className="mt-2 grid gap-2 text-sm font-bold text-slate-700 sm:grid-cols-4"><span>Produto de exemplo</span><span>2 UN na nota</span><span>R$ 12,00 total</span><span>Vincular ao estoque</span></div>
                <p className="mt-2 text-xs text-slate-500">Na nota real, revise os dados e escolha o produto correto antes de confirmar.</p>
              </div>}
              {invoiceItems.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                      <p className="text-[10px] uppercase font-black text-slate-400">Itens da nota</p>
                      <p className="text-2xl font-black text-slate-900">{invoiceItems.length}</p>
                    </div>
                    <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                      <p className="text-[10px] uppercase font-black text-emerald-700">Quantidade</p>
                      <p className="text-2xl font-black text-emerald-700">{invoiceTotalQuantity}</p>
                    </div>
                    <div className={`rounded-2xl border p-4 ${missingInvoiceLinks ? 'bg-amber-50 border-amber-100' : 'bg-cyan-50 border-cyan-100'}`}>
                      <p className={`text-[10px] uppercase font-black ${missingInvoiceLinks ? 'text-amber-700' : 'text-cyan-700'}`}>Falta vincular</p>
                      <p className={`text-2xl font-black ${missingInvoiceLinks ? 'text-amber-600' : 'text-cyan-700'}`}>{missingInvoiceLinks}</p>
                    </div>
                    <div className="rounded-2xl bg-violet-50 border border-violet-100 p-4">
                      <p className="text-[10px] uppercase font-black text-violet-700">Valor da nota</p>
                      <p className="text-xl font-black text-violet-700 whitespace-nowrap">{formatInvoiceCurrency(invoiceTotalCost)}</p>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500">Escolha por item se o estoque recebe a embalagem da nota ou as unidades dentro dela. Ao confirmar, o valor total será lançado uma única vez em <strong>Gastos › Compras e reposição</strong>.</p>
                  {invoiceDeclaredTotal > 0 && Math.abs(invoiceAmountDifference) > 0.01 && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                      Produtos: {formatInvoiceCurrency(invoiceItemsCost)}. Diferença para o valor da nota: {formatInvoiceCurrency(Math.abs(invoiceAmountDifference))}. Confira impostos, frete e demais valores antes de importar.
                    </p>
                  )}

                  {invoiceSupplier && (
                    <div className="rounded-[1.5rem] border border-cyan-100 bg-cyan-50/60 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-2xl bg-white border border-cyan-100 text-cyan-700 flex items-center justify-center shrink-0">
                          <Truck className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-widest font-black text-cyan-700">Fornecedor identificado</p>
                          <p className="font-black text-slate-900 truncate">{invoiceSupplier.name}</p>
                          <p className="text-[11px] text-slate-500 mt-1">
                            {[invoiceSupplier.document && `CNPJ/CPF ${invoiceSupplier.document}`, invoiceSupplier.phone, invoiceSupplier.email].filter(Boolean).join(' • ') || 'Dados básicos encontrados na nota'}
                          </p>
                        </div>
                      </div>
                      <label className="flex items-start gap-3 rounded-2xl bg-white border border-cyan-100 px-3 py-3 cursor-pointer md:max-w-xs">
                        <input
                          type="checkbox"
                          checked={registerInvoiceSupplier}
                          onChange={event => setRegisterInvoiceSupplier(event.target.checked)}
                          className="mt-1 h-4 w-4 accent-cyan-600"
                        />
                        <span>
                          <span className="block text-sm font-black text-slate-900">Cadastrar fornecedor</span>
                          <span className="block text-[11px] text-slate-500 mt-0.5">Salva os dados para consultar depois. É opcional.</span>
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="rounded-[1.5rem] border border-slate-200 overflow-hidden">
                    <div className="hidden md:grid grid-cols-[1.1fr_0.8fr_0.7fr_1fr] gap-3 bg-slate-50 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
                      <span>Item encontrado na nota</span>
                      <span>Quantidade</span>
                      <span>Preços da nota</span>
                      <span>Produto do estoque</span>
                    </div>
                    <div className="divide-y divide-slate-100 max-h-[42vh] overflow-y-auto">
                      {preparedInvoiceItems.map(item => {
                        const linkedProduct = stockTrackedProducts.find(product => product.id === item.productId);
                        const wasRemembered = invoiceMappings.some(mapping => mapping.supplierProductCode === item.supplierProductCode && mapping.productId === item.productId);
                        const sourceItem = invoiceItems.find(source => source.supplierProductCode === item.supplierProductCode) || item;
                        const canChooseUnits = canChooseInvoiceUnits(sourceItem.supplierProductName, sourceItem.unit);
                        const quantityChoice = invoiceQuantityChoices[item.supplierProductCode];
                        const unitsPerPackage = quantityChoice?.unitsPerPackage ?? getInvoicePackageSize(sourceItem.supplierProductName);

                        return (
                          <div key={item.supplierProductCode} className="grid md:grid-cols-[1.1fr_0.8fr_0.7fr_1fr] gap-3 p-4 items-center">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-black text-sm text-slate-900 truncate">{item.supplierProductName}</p>
                                {wasRemembered && <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-1">já lembrado</span>}
                              </div>
                              <p className="text-[11px] text-slate-400 mt-1">Código NF-e: {item.supplierProductCode}</p>
                            </div>
                            <div className="min-w-0 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">
                              <p className="text-lg font-black text-slate-900">{formatStockQuantity(item.quantity)}</p>
                              <p className="text-[10px] font-black uppercase text-slate-400">{item.unit}</p>
                              {canChooseUnits && (
                                <div className="mt-3 border-t border-slate-200 pt-3 space-y-2">
                                  <p className="text-[10px] font-black uppercase text-slate-500">Entrada no estoque</p>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className={`text-[10px] font-bold ${quantityChoice?.mode === 'unit' ? 'text-slate-400' : 'text-slate-800'}`}>Embalagem</span>
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={quantityChoice?.mode === 'unit'}
                                      aria-label={`Entrada por unidades de ${item.supplierProductName}`}
                                      onClick={() => updateInvoiceQuantityChoice(item.supplierProductCode, {
                                        mode: quantityChoice?.mode === 'unit' ? 'package' : 'unit', unitsPerPackage
                                      })}
                                      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 ${quantityChoice?.mode === 'unit' ? 'bg-emerald-600' : 'bg-slate-300'}`}
                                    >
                                      <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${quantityChoice?.mode === 'unit' ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </button>
                                    <span className={`text-[10px] font-bold ${quantityChoice?.mode === 'unit' ? 'text-emerald-700' : 'text-slate-400'}`}>Unidades</span>
                                  </div>
                                  {quantityChoice?.mode === 'unit' && (
                                    <label className="block text-[10px] font-bold text-slate-600">
                                      Unidades por {sourceItem.unit.toLowerCase()}
                                      <input
                                        type="number"
                                        min="1"
                                        max="1000"
                                        step="1"
                                        inputMode="numeric"
                                        aria-label={`Unidades por embalagem de ${item.supplierProductName}`}
                                        value={unitsPerPackage || ''}
                                        onChange={event => updateInvoiceQuantityChoice(item.supplierProductCode, {
                                          mode: 'unit', unitsPerPackage: Number(event.target.value)
                                        })}
                                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm font-bold text-slate-900"
                                      />
                                      <span className="block mt-1 text-slate-500">{formatStockQuantity(sourceItem.quantity)} {sourceItem.unit} × {isValidInvoicePackageSize(unitsPerPackage) ? unitsPerPackage : '?'} = {isValidInvoicePackageSize(unitsPerPackage) ? formatStockQuantity(sourceItem.quantity * unitsPerPackage) : '?'} UN</span>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="rounded-2xl border border-violet-100 bg-violet-50/70 px-3 py-3">
                              <p className="text-[10px] uppercase font-black text-violet-600">Unitário</p>
                              <input
                                type="number"
                                min="0"
                                step="0.0001"
                                inputMode="decimal"
                                aria-label={`Preço unitário de ${item.supplierProductName}`}
                                value={Number((item.unitPrice || 0).toFixed(4))}
                                onChange={event => updateInvoiceItemPrice(item.supplierProductCode, 'unitPrice', event.target.value)}
                                className="mt-1 w-full min-w-0 rounded-lg border border-violet-200 bg-white px-2 py-1 text-sm font-black text-violet-800 outline-none focus:border-violet-500"
                              />
                              <p className="text-[10px] uppercase font-black text-violet-600 mt-2">Total</p>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                aria-label={`Total de ${item.supplierProductName}`}
                                value={item.total || item.unitPrice * item.quantity}
                                onChange={event => updateInvoiceItemPrice(item.supplierProductCode, 'total', event.target.value)}
                                className="mt-1 w-full min-w-0 rounded-lg border border-violet-200 bg-white px-2 py-1 text-sm font-black text-violet-800 outline-none focus:border-violet-500"
                              />
                            </div>
                            <div>
                              <select
                                className={`w-full p-3 rounded-2xl outline-none text-sm font-bold border ${item.productId ? 'bg-white border-slate-200 text-slate-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                                value={item.productId}
                                onChange={event => updateInvoiceItemProduct(item.supplierProductCode, event.target.value)}
                              >
                                <option value="">Selecione o produto correto</option>
                                {stockTrackedProducts.map(product => (
                                  <option key={product.id} value={product.id}>{product.name} ({product.stockQuantity ?? 0} atual)</option>
                                ))}
                              </select>
                              {linkedProduct && <p className="text-[11px] text-slate-400 mt-2">Vai somar em: <strong>{linkedProduct.name}</strong></p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm text-amber-900">
                    <strong>Atenção:</strong> se a nota veio com “Brahma 600”, mas no seu cadastro está “Cerveja Brahma 600ml”, escolha o item certo uma vez. O código da nota fica salvo para as próximas importações.
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm text-slate-500">
                  Depois que selecionar o XML, os itens aparecem aqui para conferência.
                </div>
              )}
            </div>

            <div className="p-5 border-t border-slate-200 bg-white flex flex-col sm:flex-row gap-3 sm:justify-end flex-shrink-0">
              <Button onClick={() => setIsXmlGuideOpen(false)} variant="secondary" className="rounded-2xl">Cancelar</Button>
              <Button data-tour="stock-xml-confirm" onClick={handleConfirmInvoiceImport} disabled={tourOpen || isImportingXml || invoiceItems.length === 0 || missingInvoiceLinks > 0 || missingPackageSizes > 0} className="rounded-2xl bg-emerald-600 hover:bg-emerald-700">
                {isImportingXml ? 'Importando...' : 'Confirmar entrada no estoque'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {tourOpen && <SpotlightTour label="Guia do Estoque" steps={currentTourSteps} stepIndex={tourStep} onStepChange={changeTourStep} onFinish={finishTour} />}
    </div>
  );
};
